import React, { useState } from 'react';
import TableWindow from '../../../../components/Table/TableWindow';
import availableColumns from './WaterHazards.columns';
import useTablePages from '../../../../hooks/useTablePages';
import { useOrderBy } from '../../../../hooks/useOrderBy';
import PageBar from '../../../../components/PageBar/PageBar';
import {
  GET_WATER_HAZARD_IDS,
  GET_WATER_HAZARDS,
  WaterHazardItem,
} from './WaterHazards.query';
import { RouteComponentProps, withRouter } from 'react-router';
import Layout from '../../../../components/Layout/Layout';
import FilterBarWaterHazards, {
  defaultFilters,
  WaterHazardFilters,
} from './FilterBarWaterHazards';
import useColumnConfiguration from '../../../../hooks/useColumnConfiguration';
import useStoredFilters from '../../../../hooks/useStoredFilters';
import { PortalWithState } from 'react-portal';
import Button from '../../../../components/Button/Button';
import WaterHazardEditModal from '../../../../components/WaterHazard/WaterHazardEditModal';
import { getStringSearchOr } from '../../../../helpers/search';

import { Flex } from 'rebass';

type WaterHazardDetailParams = {
  id?: string;
};

type WaterHazardDetailProps = RouteComponentProps<WaterHazardDetailParams>;

export const searchFields = [
  {
    field: 'name',
    label: 'Name',
  },
  {
    field: 'description',
    label: 'Description',
  },
];

const convertStringSearch = (stringSearch?: string) => ({
  or: getStringSearchOr(searchFields, stringSearch),
});

const convertFilters = (storedFilters: WaterHazardFilters) => {
  return {
    hazardLevel: storedFilters.hazardLevel,
    ...convertStringSearch(storedFilters.stringSearch),
  };
};

/**
 * @component for admin water hazards page
 */
function WaterHazards({ match }: WaterHazardDetailProps): JSX.Element {
  const { storedFilters, setStoredFilters, queryFilters } =
    useStoredFilters<WaterHazardFilters>(
      convertFilters,
      'adminWaterHazards',
      defaultFilters
    );

  const { orderBy, onHeaderClick } = useOrderBy(
    'adminWaterHazards',
    availableColumns,
    'id'
  );

  const {
    loading,
    error,
    itemIds,
    selectedIds,
    setSelectedIds,
    itemLoader,
    refetch,
  } = useTablePages<WaterHazardItem>(
    'waterHazards',
    GET_WATER_HAZARD_IDS,
    {
      where: queryFilters,
      limit: null,
      orderBy,
    },
    GET_WATER_HAZARDS,
    (ids) => ({
      where: {
        id: ids,
      },
      limit: null,
      orderBy,
    })
  );

  const { visibleColumns, columnConfiguration, setColumnConfiguration } =
    useColumnConfiguration(
      availableColumns,
      'storedColumnConfigurationAdminWaterHazards'
    );

  const [selectedHazard, setSelectedHazard] = useState<WaterHazardItem>();

  return (
    <Layout>
      <Flex
        flexGrow={1}
        flexDirection="column"
        px="mainContent"
        pb="mainContent"
      >
        <PageBar title="Water Hazards">
          <PortalWithState closeOnEsc>
            {({ openPortal, closePortal, portal }) => (
              <>
                <Button
                  ml={3}
                  variant="primary"
                  icon="plus"
                  onClick={openPortal}
                >
                  Add Hazard
                </Button>
                {portal(
                  <WaterHazardEditModal
                    action="add"
                    onClose={() => {
                      refetch();
                      closePortal();
                    }}
                  />
                )}
              </>
            )}
          </PortalWithState>
        </PageBar>
        <FilterBarWaterHazards
          filters={storedFilters}
          setFilters={setStoredFilters}
          selectedId={match.params.id}
        />
        {loading && itemIds.length === 0 ? (
          'Loading...'
        ) : (
          <PortalWithState closeOnEsc>
            {({ openPortal, closePortal, portal }) => {
              const onRowClick = (item: WaterHazardItem) => {
                setSelectedHazard(item);
                openPortal();
              };
              return (
                <>
                  <TableWindow
                    columns={visibleColumns}
                    itemIds={itemIds}
                    itemLoader={itemLoader}
                    onSelectionChange={setSelectedIds}
                    selectedIds={selectedIds}
                    activeIds={match.params.id ? [match.params.id] : undefined}
                    onHeaderClick={onHeaderClick}
                    orderBy={orderBy}
                    onRowClick={onRowClick}
                    columnConfiguration={columnConfiguration}
                    setColumnConfiguration={setColumnConfiguration}
                  />
                  {portal(
                    <>
                      {selectedHazard && (
                        <WaterHazardEditModal
                          onClose={() => {
                            refetch();
                            closePortal();
                          }}
                          action="edit"
                          waterHazardId={selectedHazard.id}
                          waterHazard={selectedHazard}
                        />
                      )}
                    </>
                  )}
                </>
              );
            }}
          </PortalWithState>
        )}
      </Flex>
    </Layout>
  );
}

export default withRouter(WaterHazards);
